import {
  Box,
  Container,
  Stack,
  Typography,
  Card,
  CardContent,
  Rating,
  Divider,
  Chip,
  Button,
  Snackbar,
  TextField,
  InputAdornment,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useState, useMemo } from 'react';

import { useGetCostEstimate } from '@/api/endpoints/vendor/useGetCostEstimate';
import { useGetDiyVideo } from '@/api/endpoints/vendor/useGetDiyVideo';
import { useGetPermitRequirements } from '@/api/endpoints/vendor/useGetPermitRequirements';
import { useGetVendorQuestions } from '@/api/endpoints/vendor/useGetVendorQuestions';
import { useGetVendorsBySearchId } from '@/api/endpoints/vendor/useGetVendorsBySearchId';
import {
  VendorSearchResult,
  DiyVideo,
  CostEstimate,
  PermitRequirement,
  ProjectReadinessChecklist,
  VendorQuestions,
} from '@/api/models/vendor';
import { ArrowDownIcon } from '@/assets/icons/ArrowDownIcon';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { ArrowUpIcon } from '@/assets/icons/ArrowUpIcon';
import { Edit05Icon } from '@/assets/icons/Edit05Icon';
import { Globe02Icon } from '@/assets/icons/Globe02Icon';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';
import { MarkerPin01Icon } from '@/assets/icons/MarkerPin01Icon';
import { Phone01Icon } from '@/assets/icons/Phone01Icon';
import { SearchLgIcon } from '@/assets/icons/SearchLgIcon';
import costEstimateLoader from '@/assets/loaders/cost-estimate-loader.svg';
import diyVideoLoader from '@/assets/loaders/diy-video-loader.svg';
import permitLoader from '@/assets/loaders/permit-loader.svg';
import questionsLoader from '@/assets/loaders/questions-loader.svg';

import BaseLottie from '@/components/lottie/BaseLottie';
import HowWeRankVendors from '@/components/vendor/HowWeRankVendors';

// eslint-disable-next-line @nx/enforce-module-boundaries
import vendorSearchAnimation from '../../../public/assets/images/vendor_search.json';

export interface VendorSearchResultsProps {
  vendors: VendorSearchResult[];
  searchQuery: string;
  address: string;
  disclaimer?: string;
  search_query?: string;
  searchId?: string;
  isAuthenticated: boolean;
  onNewSearch: () => void;
  onEditSearch?: (newQuery: string) => void;
  onCallVendors: (
    selectedVendors: string[],
    vendorDetails: VendorSearchResult[]
  ) => Promise<void>;
  onAuthRequired?: () => void;
  isCallInProgress?: boolean;
  showBackground?: boolean;
  containerMaxWidth?: 'lg' | 'xl' | 'md';
  wrapperStyles?: object;
  calledVendors?: string[];
  initialSelectedVendors?: string[];
  diyVideo?: DiyVideo | null;
  costEstimate?: CostEstimate | null;
  permitRequirement?: PermitRequirement | null;
  projectReadiness?: ProjectReadinessChecklist | null;
  vendorQuestions?: VendorQuestions | null;
}

const VendorSearchResults: React.FC<VendorSearchResultsProps> = ({
  vendors = [],
  searchQuery = '',
  address = '',
  disclaimer,
  search_query,
  searchId,
  isAuthenticated,
  onNewSearch,
  onEditSearch,
  onCallVendors,
  onAuthRequired,
  isCallInProgress = false,
  showBackground = true,
  containerMaxWidth = 'lg',
  wrapperStyles = {},
  calledVendors: externalCalledVendors = [],
  initialSelectedVendors = [],
  diyVideo = null,
  costEstimate = null,
  permitRequirement = null,
  projectReadiness = null,
  vendorQuestions = null,
}) => {
  const [sortBy, setSortBy] = useState<'match_score' | 'distance' | 'name'>('distance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [isEditingSearch, setIsEditingSearch] = useState(false);
  const [editedSearchQuery, setEditedSearchQuery] = useState(searchQuery);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch stored vendor data with calculated distances if we have a searchId
  const { data: storedVendors } = useGetVendorsBySearchId(searchId || '', !!searchId);

  // Lazy loading hooks for enrichments - only fetch when tab is active
  const enrichmentParams = {
    query: searchQuery,
    service_type: search_query || searchQuery,
    location: address,
    requirements: undefined,
  };

  // Enable hooks based on active tab
  const costEstimateQuery = useGetCostEstimate(enrichmentParams, activeTab === 1);
  const permitRequirementsQuery = useGetPermitRequirements(enrichmentParams, activeTab === 2);
  const vendorQuestionsQuery = useGetVendorQuestions(enrichmentParams, activeTab === 3);
  const diyVideoQuery = useGetDiyVideo(enrichmentParams, activeTab === 4);

  // Use lazy-loaded data or fallback to props
  const lazyLoadedCostEstimate = costEstimateQuery.data?.data || null;
  const lazyLoadedPermitRequirement = permitRequirementsQuery.data?.data || null;
  const lazyLoadedVendorQuestions = vendorQuestionsQuery.data?.data || null;
  const lazyLoadedDiyVideo = diyVideoQuery.data?.data || null;

  // Merge original vendor data with stored data to get calculated distances
  const enhancedVendors = useMemo(() => {
    if (!storedVendors || !searchId) {
      return vendors; // Use original data if no stored data available
    }

    return vendors.map(originalVendor => {
      // Find matching stored vendor by name and address
      const storedVendor = storedVendors.find(
        stored => stored.name === originalVendor.name && stored.address === originalVendor.address
      );

      if (storedVendor) {
        // Merge calculated distance fields
        return {
          ...originalVendor,
          distance_meters: storedVendor.distance_meters,
          distance_formatted: storedVendor.distance_formatted,
          distance_miles: storedVendor.distance_miles,
        };
      }

      return originalVendor; // Return original if no stored match found
    });
  }, [vendors, storedVendors, searchId]);

  const parseDistance = (vendor: VendorSearchResult): number => {
    // Use accurate distance_miles if available, otherwise parse the distance string
    if (vendor.distance_miles !== undefined && vendor.distance_miles !== null) {
      return vendor.distance_miles;
    }

    // Handle "Local service area" - treat as very close (0.1 miles for sorting)
    if (
      vendor.distance &&
      (vendor.distance.toLowerCase().includes('local') ||
        vendor.distance.toLowerCase().includes('service area'))
    ) {
      return 0.1; // Very close for sorting purposes
    }

    const match = vendor.distance.match(/([0-9.]+)/);
    return match ? parseFloat(match[1]) : 999; // High number for unknown distances (sort to end)
  };

  const formatDistanceDisplay = (vendor: VendorSearchResult): string => {
    // Always prefer calculated distances over AI-provided strings
    if (vendor.distance_formatted) {
      return vendor.distance_formatted;
    }
    if (vendor.distance_miles !== undefined && vendor.distance_miles !== null) {
      return `${vendor.distance_miles.toFixed(1)} mi`;
    }

    // Handle "Local service area" - show as nearby
    if (
      vendor.distance &&
      (vendor.distance.toLowerCase().includes('local') ||
        vendor.distance.toLowerCase().includes('service area'))
    ) {
      return 'Nearby'; // More user-friendly than "Local service area"
    }

    // Handle other distance strings that contain numbers
    if (vendor.distance && vendor.distance !== 'null' && /\d/.test(vendor.distance)) {
      return vendor.distance;
    }

    // Final fallback
    return 'Distance calculating...';
  };

  const sortedResults = useMemo(() => {
    const sorted = [...enhancedVendors].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
      case 'match_score':
        comparison = a.match_score - b.match_score;
        break;
      case 'distance':
        comparison = parseDistance(a) - parseDistance(b);
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      default:
        comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [enhancedVendors, sortBy, sortOrder]);

  // Always show all tabs - data loads on demand
  const availableTabs = useMemo(() => [
    { label: 'Providers', value: 'providers' },
    { label: 'Cost Estimate', value: 'cost-estimate' },
    { label: 'Permit Info', value: 'permit-requirements' },
    { label: 'Questions', value: 'vendor-questions' },
    { label: 'DIY Video', value: 'diy-video' },
  ], []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSortChange = (field: 'match_score' | 'distance' | 'name') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'name' || field === 'distance' ? 'asc' : 'desc');
    }
  };

  const getSortIcon = (field: 'match_score' | 'distance' | 'name') => {
    if (sortBy !== field) return undefined;
    const IconComponent = sortOrder === 'desc' ? ArrowDownIcon : ArrowUpIcon;
    return <IconComponent width={16} height={16} />;
  };

  const handleEditSearch = () => {
    setIsEditingSearch(true);
    setEditedSearchQuery(searchQuery);
  };

  const handleCancelEdit = () => {
    setIsEditingSearch(false);
    setEditedSearchQuery(searchQuery);
  };

  const handleSearchSubmit = () => {
    if (editedSearchQuery.trim() && onEditSearch) {
      onEditSearch(editedSearchQuery.trim());
      setIsEditingSearch(false);
    }
  };

  const defaultWrapperStyles = showBackground
    ? {
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #FFF9F5 0%, #FFFFFF 100%)',
      py: { xs: 4, md: 6 },
    }
    : { py: { xs: 2, md: 3 } };

  return (
    <Box sx={{ ...defaultWrapperStyles, ...wrapperStyles }}>
      <Container maxWidth={containerMaxWidth}>
        <Stack spacing={2.5}>
          {/* Loading Animation - same as VendorSearchSection */}
          {isCallInProgress && (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                width: '100%',
                maxWidth: '800px',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                mx: 'auto',
              }}
            >
              <Box sx={{ width: 200, height: 200 }}>
                <BaseLottie animationData={vendorSearchAnimation} loop autoplay />
              </Box>
              <Stack spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Deploying our AI agent to scout the perfect providers...
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Our digital detective is on the case! Sit tight while we match you with top
                  providers tailored to your needs.
                </Typography>
              </Stack>
            </Paper>
          )}

          {/* Main Content - hidden when loading */}
          {!isCallInProgress && (
            <>
              {/* Header */}
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    startIcon={<ArrowLeftIcon width={16} height={16} />}
                    onClick={onNewSearch}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      px: 1.5,
                      py: 0.5,
                      minWidth: 'auto',
                    }}
                  >
                    New Search
                  </Button>
                  {onEditSearch && (
                    <Button
                      startIcon={<Edit05Icon width={16} height={16} />}
                      onClick={handleEditSearch}
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                      }}
                    >
                      Edit Search
                    </Button>
                  )}
                </Stack>

                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
                  >
                    Provider Search Results
                  </Typography>
                  {isEditingSearch ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <TextField
                        value={editedSearchQuery}
                        onChange={e => setEditedSearchQuery(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            handleSearchSubmit();
                          }
                        }}
                        variant="outlined"
                        size="small"
                        placeholder="Enter search query..."
                        sx={{ flex: 1, maxWidth: 400 }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchLgIcon width={16} height={16} />
                              </InputAdornment>
                            ),
                          },
                        }}
                        autoFocus
                      />
                      <Button
                        variant="contained"
                        onClick={handleSearchSubmit}
                        disabled={!editedSearchQuery.trim()}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Search
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Found {vendors.length} provider{vendors.length !== 1 ? 's' : ''} for "
                        {searchQuery}"
                      </Typography>
                      {address && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <MarkerPin01Icon width={14} height={14} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            Near {address}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>

              {/* Disclaimer and Search Query Info */}
              {(disclaimer || search_query) && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Stack spacing={0.5}>
                    {disclaimer && (
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        {disclaimer}
                      </Typography>
                    )}
                    {search_query && search_query !== searchQuery && (
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', fontSize: '0.875rem', color: 'text.secondary' }}
                      >
                        Search refined to: "{search_query}"
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Tabs Navigation */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 40,
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      minWidth: { xs: 100, sm: 120 },
                      minHeight: 48,
                      py: 1.5,
                      px: { xs: 2, sm: 2.5 },
                      color: 'text.secondary',
                      '&.Mui-selected': {
                        color: 'text.primary',
                        fontWeight: 700,
                      },
                    },
                    '& .MuiTabs-indicator': {
                      height: 3,
                      bgcolor: 'text.primary',
                    },
                    '& .MuiTabs-scrollButtons': {
                      width: { xs: 32, sm: 40 },
                      '&.Mui-disabled': {
                        opacity: 0.3,
                      },
                    },
                    '& .MuiTabs-scrollButtons.MuiTabs-scrollButtons': {
                      color: 'text.primary',
                      bgcolor: { xs: 'rgba(255, 255, 255, 0.9)', sm: 'transparent' },
                      borderRadius: { xs: 1, sm: 0 },
                      mx: { xs: 0.5, sm: 0 },
                      '&:hover': {
                        bgcolor: { xs: 'rgba(0, 0, 0, 0.04)', sm: 'transparent' },
                      },
                    },
                    '& .MuiTabs-scroller': {
                      overflowX: { xs: 'auto !important', sm: 'hidden' },
                    },
                  }}
                >
                  {availableTabs.map((tab, index) => (
                    <Tab key={tab.value} label={tab.label} id={`tab-${index}`} />
                  ))}
                </Tabs>
              </Box>

              {/* Tab Panels */}
              {/* Providers Tab */}
              {activeTab === availableTabs.findIndex(tab => tab.value === 'providers') && (
                <Stack spacing={2}>
                  {/* How We Rank Providers Section - Collapsible */}
                  <HowWeRankVendors />

                  {/* Provider Status Summary */}
                  {externalCalledVendors.length > 0 && (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Phone01Icon width={16} height={16} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        <strong>
                          Terri is reaching out to {externalCalledVendors.length} provider
                          {externalCalledVendors.length > 1 ? 's' : ''}
                        </strong>
                      </Typography>
                    </Box>
                  )}

                  {/* Sorting Controls */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 2 }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ py: 1.5 }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}
                      >
                        Sort by:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          label="Match Score"
                          onClick={() => handleSortChange('match_score')}
                          variant={sortBy === 'match_score' ? 'filled' : 'outlined'}
                          icon={getSortIcon('match_score')}
                          size="small"
                          sx={{
                            bgcolor: sortBy === 'match_score' ? 'text.primary' : 'transparent',
                            color: sortBy === 'match_score' ? 'white' : 'text.primary',
                            borderColor: 'text.primary',
                            fontSize: '0.75rem',
                            height: 28,
                            '&:hover': {
                              bgcolor: sortBy === 'match_score' ? 'text.primary' : 'grey.100',
                            },
                          }}
                        />
                        <Chip
                          label="Distance"
                          onClick={() => handleSortChange('distance')}
                          variant={sortBy === 'distance' ? 'filled' : 'outlined'}
                          icon={getSortIcon('distance')}
                          size="small"
                          sx={{
                            bgcolor: sortBy === 'distance' ? 'text.primary' : 'transparent',
                            color: sortBy === 'distance' ? 'white' : 'text.primary',
                            borderColor: 'text.primary',
                            fontSize: '0.75rem',
                            height: 28,
                            '&:hover': {
                              bgcolor: sortBy === 'distance' ? 'text.primary' : 'grey.100',
                            },
                          }}
                        />
                        <Chip
                          label="Name"
                          onClick={() => handleSortChange('name')}
                          variant={sortBy === 'name' ? 'filled' : 'outlined'}
                          icon={getSortIcon('name')}
                          size="small"
                          sx={{
                            bgcolor: sortBy === 'name' ? 'text.primary' : 'transparent',
                            color: sortBy === 'name' ? 'white' : 'text.primary',
                            borderColor: 'text.primary',
                            fontSize: '0.75rem',
                            height: 28,
                            '&:hover': {
                              bgcolor: sortBy === 'name' ? 'text.primary' : 'grey.100',
                            },
                          }}
                        />
                      </Stack>
                    </Stack>

                    {/* Provider Count Summary */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    >
                      {vendors.length} provider{vendors.length !== 1 ? 's' : ''} found
                    </Typography>
                  </Stack>

                  {/* Provider List - Table View */}
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: 'white',
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <TableCell sx={{ borderBottom: 'none', py: 2 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary' }}
                            >
                              Provider
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, borderBottom: 'none', py: 2 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary' }}
                            >
                              Services
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, borderBottom: 'none', py: 2 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary' }}
                            >
                              Contact
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, borderBottom: 'none', py: 2 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary' }}
                            >
                              Distance
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderBottom: 'none', py: 2 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary' }}
                            >
                              Match
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedResults.map(vendor => {
                          const vendorId = `${vendor.name}-${vendor.address}`;
                          const isCalled = externalCalledVendors.includes(vendorId);
                          return (
                            <TableRow
                              key={vendorId}
                              sx={{
                                '&:hover': {
                                  bgcolor: 'grey.50',
                                },
                                bgcolor: 'white',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:last-child': {
                                  borderBottom: 0,
                                },
                              }}
                            >
                              {/* Provider Info */}
                              <TableCell sx={{ py: 3 }}>
                                <Stack spacing={0.5}>
                                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                                    <Typography
                                      variant="body1"
                                      fontWeight={600}
                                      sx={{
                                        color: 'text.primary',
                                        fontSize: '0.95rem',
                                      }}
                                    >
                                      {vendor.name}
                                    </Typography>
                                    {isCalled && (
                                      <Box
                                        component="span"
                                        sx={{
                                          px: 1,
                                          py: 0.25,
                                          border: '1px solid',
                                          borderColor: 'text.secondary',
                                          fontSize: '0.65rem',
                                          fontWeight: 500,
                                          color: 'text.secondary',
                                          textTransform: 'uppercase',
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Calling
                                      </Box>
                                    )}
                                    {vendor.emergency_service && (
                                      <Box
                                        component="span"
                                        sx={{
                                          px: 1,
                                          py: 0.25,
                                          border: '1px solid',
                                          borderColor: 'text.primary',
                                          fontSize: '0.65rem',
                                          fontWeight: 500,
                                          color: 'text.primary',
                                          textTransform: 'uppercase',
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        24/7
                                      </Box>
                                    )}
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary">
                                    {vendor.address !== 'null' ? vendor.address : 'No address'}
                                  </Typography>
                                  {/* Show on mobile */}
                                  <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Phone01Icon width={12} height={12} />
                                      <Typography variant="caption">{vendor.phone}</Typography>
                                    </Stack>
                                  </Box>
                                </Stack>
                              </TableCell>

                              {/* Services */}
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, py: 3 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    maxWidth: 250,
                                    color: 'text.secondary',
                                    lineHeight: 1.5,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {vendor.services}
                                </Typography>
                              </TableCell>

                              {/* Contact */}
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, py: 3 }}>
                                <Stack spacing={0.5}>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Phone01Icon width={14} height={14} />
                                    <Typography variant="body2" fontWeight={500}>
                                      {vendor.phone}
                                    </Typography>
                                  </Stack>
                                  {vendor.email && vendor.email !== 'null' && (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Mail01Icon width={14} height={14} />
                                      <Typography
                                        variant="caption"
                                        component="a"
                                        href={`mailto:${vendor.email}`}
                                        sx={{
                                          color: 'text.primary',
                                          textDecoration: 'underline',
                                          '&:hover': {
                                            color: 'text.secondary',
                                          },
                                        }}
                                      >
                                        Email
                                      </Typography>
                                    </Stack>
                                  )}
                                  {vendor.website && vendor.website !== 'null' && (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Globe02Icon width={14} height={14} />
                                      <Typography
                                        variant="caption"
                                        component="a"
                                        href={vendor.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          color: 'text.primary',
                                          textDecoration: 'underline',
                                          '&:hover': {
                                            color: 'text.secondary',
                                          },
                                        }}
                                      >
                                        Website
                                      </Typography>
                                    </Stack>
                                  )}
                                </Stack>
                              </TableCell>

                              {/* Distance */}
                              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, py: 3 }}>
                                {vendor.distance && vendor.distance !== 'null' && (
                                  <Stack spacing={0.25}>
                                    <Typography
                                      variant="body1"
                                      fontWeight={600}
                                      sx={{ color: 'text.primary' }}
                                    >
                                      {formatDistanceDisplay(vendor)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.7rem',
                                      }}
                                    >
                                      {vendor.distance_meters !== null &&
                                      vendor.distance_meters !== undefined
                                        ? 'Precise'
                                        : 'Estimated'}
                                    </Typography>
                                  </Stack>
                                )}
                              </TableCell>

                              {/* Match Score */}
                              <TableCell align="right" sx={{ py: 3 }}>
                                <Stack alignItems="flex-end" spacing={0.5}>
                                  <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{ color: 'text.primary', lineHeight: 1 }}
                                  >
                                    {(vendor.match_score * 100).toFixed(0)}%
                                  </Typography>
                                  <Rating
                                    value={vendor.match_score * 5}
                                    max={5}
                                    precision={0.5}
                                    readOnly
                                    size="small"
                                    sx={{
                                      '& .MuiRating-iconFilled': {
                                        color: 'text.primary',
                                      },
                                      '& .MuiRating-iconEmpty': {
                                        color: 'grey.300',
                                      },
                                    }}
                                  />
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}

              {/* Cost Estimate Tab */}
              {activeTab === availableTabs.findIndex(tab => tab.value === 'cost-estimate') && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'white',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Loading state */}
                    {costEstimateQuery.isLoading && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 8,
                        }}
                      >
                        <Box
                          component="img"
                          src={costEstimateLoader}
                          alt="Calculating cost estimate"
                          sx={{
                            width: 200,
                            height: 200,
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 2 }}
                        >
                          Calculating cost estimate...
                        </Typography>
                      </Box>
                    )}

                    {/* Error state */}
                    {costEstimateQuery.isError && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="error" gutterBottom>
                          Failed to load cost estimate
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Please try again later
                        </Typography>
                      </Box>
                    )}

                    {/* Not applicable state */}
                    {!costEstimateQuery.isLoading &&
                      !costEstimateQuery.isError &&
                      !lazyLoadedCostEstimate && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          💰 Cost Estimate Not Available
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cost estimates are not available for this type of service
                        </Typography>
                      </Box>
                    )}

                    {/* Data loaded state */}
                    {lazyLoadedCostEstimate && (
                      <Stack spacing={2.5}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.5rem',
                            }}
                          >
                          💰
                          </Box>
                          <Stack flex={1}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                              }}
                            >
                            Estimated Cost
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mt: 0.5,
                              }}
                            >
                            Based on current market rates in your area
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack spacing={2}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="baseline"
                          >
                            <Typography variant="body2" color="text.secondary">
                            Typical Cost:
                            </Typography>
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 700,
                                color: 'primary.main',
                              }}
                            >
                            ${lazyLoadedCostEstimate.typical.toLocaleString()}
                            </Typography>
                          </Stack>

                          <Divider />

                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2" color="text.secondary">
                            Price Range:
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                              }}
                            >
                            ${lazyLoadedCostEstimate.low.toLocaleString()} - $
                              {lazyLoadedCostEstimate.high.toLocaleString()}
                            </Typography>
                          </Stack>

                          {lazyLoadedCostEstimate.unit !== 'total' && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic' }}
                            >
                            Per {lazyLoadedCostEstimate.unit.replace('per_', '').replace('_', ' ')}
                            </Typography>
                          )}
                        </Stack>

                        {lazyLoadedCostEstimate.factors &&
                        lazyLoadedCostEstimate.factors.length > 0 && (
                          <>
                            <Divider />
                            <Stack spacing={1}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.primary"
                              >
                              Factors affecting price:
                              </Typography>
                              <Stack spacing={0.5}>
                                {lazyLoadedCostEstimate.factors.map((factor) => (
                                  <Typography
                                    key={factor}
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ pl: 2 }}
                                  >
                                  • {factor}
                                  </Typography>
                                ))}
                              </Stack>
                            </Stack>
                          </>
                        )}

                        {lazyLoadedCostEstimate.cost_breakdown && (
                          <>
                            <Divider />
                            <Stack spacing={1}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.primary"
                              >
                              📊 Detailed Cost Breakdown:
                              </Typography>
                              <Stack spacing={1}>
                                {lazyLoadedCostEstimate.cost_breakdown.labor != null && (
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                    Labor:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                    ${lazyLoadedCostEstimate.cost_breakdown.labor.toLocaleString()}
                                    </Typography>
                                  </Stack>
                                )}
                                {lazyLoadedCostEstimate.cost_breakdown.materials != null && (
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                    Materials:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                    $
                                      {lazyLoadedCostEstimate
                                        .cost_breakdown.materials.toLocaleString()}
                                    </Typography>
                                  </Stack>
                                )}
                                {lazyLoadedCostEstimate.cost_breakdown.permits != null && (
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                    Permits:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                    $
                                      {lazyLoadedCostEstimate
                                        .cost_breakdown.permits.toLocaleString()}
                                    </Typography>
                                  </Stack>
                                )}
                                {lazyLoadedCostEstimate.cost_breakdown.equipment != null && (
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                    Equipment:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                    $
                                      {lazyLoadedCostEstimate
                                        .cost_breakdown.equipment.toLocaleString()}
                                    </Typography>
                                  </Stack>
                                )}
                                {lazyLoadedCostEstimate.cost_breakdown.other != null && (
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                    Other:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                    ${lazyLoadedCostEstimate.cost_breakdown.other.toLocaleString()}
                                    </Typography>
                                  </Stack>
                                )}
                              </Stack>
                            </Stack>
                          </>
                        )}

                        {lazyLoadedCostEstimate.seasonal_insight && (
                          <>
                            <Divider />
                            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                gutterBottom
                                color="text.primary"
                              >
                              📅 Seasonal Savings Tip:
                              </Typography>
                              {lazyLoadedCostEstimate.seasonal_insight.best_months.length > 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  <strong>Best months to save:</strong>{' '}
                                  {lazyLoadedCostEstimate.seasonal_insight.best_months.join(', ')}
                                </Typography>
                              )}
                              {lazyLoadedCostEstimate.seasonal_insight.savings_potential && (
                                <Typography variant="body2" color="text.primary" fontWeight={600}>
                                  {lazyLoadedCostEstimate.seasonal_insight.savings_potential}
                                </Typography>
                              )}
                              {lazyLoadedCostEstimate.seasonal_insight.reason && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}
                                >
                                  {lazyLoadedCostEstimate.seasonal_insight.reason}
                                </Typography>
                              )}
                            </Box>
                          </>
                        )}

                        {lazyLoadedCostEstimate.price_outlier_warning && (
                          <>
                            <Divider />
                            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                gutterBottom
                                color="text.primary"
                              >
                              ⚠️ Price Outlier Warning:
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {lazyLoadedCostEstimate.price_outlier_warning.message}
                              </Typography>
                            </Box>
                          </>
                        )}

                        {lazyLoadedCostEstimate.material_costs &&
                        lazyLoadedCostEstimate.material_costs.length > 0 && (
                          <>
                            <Divider />
                            <Stack spacing={1}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.primary"
                              >
                              🏗️ Real-Time Material Costs:
                              </Typography>
                              <Stack spacing={1.5}>
                                {lazyLoadedCostEstimate.material_costs.map((material) => (
                                  <Stack key={material.material_name} spacing={0.5}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Typography variant="body2" fontWeight={600}>
                                        {material.material_name}
                                      </Typography>
                                      <Chip
                                        label={material.trend}
                                        size="small"
                                        sx={{
                                          bgcolor: 'grey.100',
                                          color: 'text.primary',
                                          fontSize: '0.7rem',
                                        }}
                                      />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                      {material.current_price}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                    Updated: {material.last_updated}
                                    </Typography>
                                  </Stack>
                                ))}
                              </Stack>
                            </Stack>
                          </>
                        )}

                        {lazyLoadedCostEstimate.diy_vs_contractor && (
                          <>
                            <Divider />
                            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                gutterBottom
                                color="text.primary"
                              >
                              🔨 DIY vs Hiring a Contractor:
                              </Typography>
                              <Stack spacing={1.5} sx={{ mt: 1 }}>
                                <Stack direction="row" justifyContent="space-between">
                                  <Stack flex={1}>
                                    <Typography variant="caption" color="text.secondary">
                                    DIY Cost
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                    $
                                      {
                                        lazyLoadedCostEstimate.diy_vs_contractor.diy_cost_low
                                          .toLocaleString()
                                      }
                                      {' - '}
                                    $
                                      {
                                        lazyLoadedCostEstimate.diy_vs_contractor.diy_cost_high
                                          .toLocaleString()
                                      }
                                    </Typography>
                                  </Stack>
                                  <Stack flex={1}>
                                    <Typography variant="caption" color="text.secondary">
                                    Contractor Cost
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                    $
                                      {
                                        lazyLoadedCostEstimate.diy_vs_contractor
                                          .contractor_cost_low.toLocaleString()
                                      }
                                      {' - '}
                                    $
                                      {
                                        lazyLoadedCostEstimate.diy_vs_contractor
                                          .contractor_cost_high.toLocaleString()
                                      }
                                    </Typography>
                                  </Stack>
                                </Stack>

                                <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1.5 }}>
                                  <Typography variant="body2" fontWeight={600}>
                                  Potential savings: $
                                    {
                                      lazyLoadedCostEstimate.diy_vs_contractor.savings_amount
                                        .toLocaleString()
                                    }
                                    {' '}
                                  (
                                    {lazyLoadedCostEstimate.diy_vs_contractor.savings_percentage})
                                  </Typography>
                                </Box>

                                <Stack spacing={0.5}>
                                  <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">
                                    Estimated time:
                                    </Typography>
                                    <Typography variant="caption" fontWeight={600}>
                                      {lazyLoadedCostEstimate.diy_vs_contractor.diy_time_estimate}
                                    </Typography>
                                  </Stack>
                                  <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">
                                    Skill level:
                                    </Typography>
                                    <Chip
                                      label={
                                        lazyLoadedCostEstimate.diy_vs_contractor
                                          .skill_level_required
                                      }
                                      size="small"
                                      sx={{
                                        bgcolor: 'grey.100',
                                        color: 'text.primary',
                                        fontSize: '0.7rem',
                                      }}
                                    />
                                  </Stack>
                                </Stack>

                                {lazyLoadedCostEstimate.diy_vs_contractor.notes && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic', display: 'block' }}
                                  >
                                    {lazyLoadedCostEstimate.diy_vs_contractor.notes}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                          </>
                        )}

                        {lazyLoadedCostEstimate.notes && (
                          <>
                            <Divider />
                            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                              💡 {lazyLoadedCostEstimate.notes}
                              </Typography>
                            </Box>
                          </>
                        )}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            textAlign: 'center',
                            fontStyle: 'italic',
                          }}
                        >
                        Confidence: {lazyLoadedCostEstimate.confidence} • Estimates are
                        approximate and may vary based on specific requirements
                        </Typography>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* DIY Video Tab */}
              {activeTab === availableTabs.findIndex(tab => tab.value === 'diy-video') && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'white',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Loading state */}
                    {diyVideoQuery.isLoading && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 8,
                        }}
                      >
                        <Box
                          component="img"
                          src={diyVideoLoader}
                          alt="Finding DIY tutorial"
                          sx={{
                            width: 200,
                            height: 200,
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 2 }}
                        >
                          Finding DIY tutorial...
                        </Typography>
                      </Box>
                    )}

                    {/* Error state */}
                    {diyVideoQuery.isError && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="error">
                          Failed to load DIY video
                        </Typography>
                      </Box>
                    )}

                    {/* Not applicable state */}
                    {!diyVideoQuery.isLoading &&
                      !diyVideoQuery.isError &&
                      !lazyLoadedDiyVideo && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6">🎥 DIY Video Not Available</Typography>
                        <Typography variant="body2" color="text.secondary">
                          DIY videos are not available for this type of service
                        </Typography>
                      </Box>
                    )}

                    {/* Data loaded state */}
                    {lazyLoadedDiyVideo && (
                      <Stack spacing={3}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.5rem',
                            }}
                          >
                            🎥
                          </Box>
                          <Stack flex={1}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 700, color: 'text.primary' }}
                            >
                              DIY Tutorial Video
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', mt: 0.5 }}
                            >
                              Learn how to handle this project yourself
                            </Typography>
                          </Stack>
                        </Stack>

                        {lazyLoadedDiyVideo.url && (
                          <Box
                            sx={{
                              position: 'relative',
                              paddingTop: '56.25%',
                              borderRadius: 2,
                              overflow: 'hidden',
                            }}
                          >
                            <iframe
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                              }}
                              src={lazyLoadedDiyVideo.url}
                              title={lazyLoadedDiyVideo.title || 'DIY Tutorial'}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </Box>
                        )}

                        <Stack spacing={1.5}>
                          {lazyLoadedDiyVideo.title && (
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: 'text.primary' }}
                            >
                              {lazyLoadedDiyVideo.title}
                            </Typography>
                          )}
                          {lazyLoadedDiyVideo.description && (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              {lazyLoadedDiyVideo.description}
                            </Typography>
                          )}
                          {lazyLoadedDiyVideo.duration && (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              Duration: {lazyLoadedDiyVideo.duration}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Permit Requirements Tab */}
              {activeTab === availableTabs.findIndex(tab => tab.value === 'permit-requirements') && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'white',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Loading state */}
                    {permitRequirementsQuery.isLoading && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 8,
                        }}
                      >
                        <Box
                          component="img"
                          src={permitLoader}
                          alt="Checking permit requirements"
                          sx={{
                            width: 200,
                            height: 200,
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 2 }}
                        >
                          Checking permit requirements...
                        </Typography>
                      </Box>
                    )}

                    {/* Error state */}
                    {permitRequirementsQuery.isError && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="error">
                          Failed to load permit requirements
                        </Typography>
                      </Box>
                    )}

                    {/* Not applicable state */}
                    {!permitRequirementsQuery.isLoading &&
                      !permitRequirementsQuery.isError &&
                      (!lazyLoadedPermitRequirement ||
                        !lazyLoadedPermitRequirement.is_required) && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6">📋 No Permit Required</Typography>
                        <Typography variant="body2" color="text.secondary">
                          This project does not require a permit
                        </Typography>
                      </Box>
                    )}

                    {/* Data loaded state */}
                    {lazyLoadedPermitRequirement &&
                      lazyLoadedPermitRequirement.is_required && (
                      <Stack spacing={2.5}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.5rem',
                            }}
                          >
                            📋
                          </Box>
                          <Stack flex={1}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 700, color: 'text.primary' }}
                            >
                              Permit Required
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', mt: 0.5 }}
                            >
                              This project requires a permit from your local township
                            </Typography>
                          </Stack>
                        </Stack>

                        <Divider />

                        <Stack spacing={2}>
                          {lazyLoadedPermitRequirement.permit_type && (
                            <Stack spacing={0.5}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.primary"
                              >
                                Permit Type:
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                {lazyLoadedPermitRequirement.permit_type}
                              </Typography>
                            </Stack>
                          )}

                          {lazyLoadedPermitRequirement.township_name && (
                            <Stack spacing={0.5}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.primary"
                              >
                                Township:
                              </Typography>
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {lazyLoadedPermitRequirement.township_name}
                              </Typography>
                            </Stack>
                          )}

                          {(lazyLoadedPermitRequirement.estimated_cost ||
                            lazyLoadedPermitRequirement.processing_time) && (
                            <>
                              <Divider sx={{ my: 1 }} />
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                              >
                                {lazyLoadedPermitRequirement.estimated_cost && (
                                  <Stack spacing={0.5} flex={1}>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      color="text.primary"
                                    >
                                      Estimated Cost:
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {lazyLoadedPermitRequirement.estimated_cost}
                                    </Typography>
                                  </Stack>
                                )}
                                {lazyLoadedPermitRequirement.processing_time && (
                                  <Stack spacing={0.5} flex={1}>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      color="text.primary"
                                    >
                                      Processing Time:
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {lazyLoadedPermitRequirement.processing_time}
                                    </Typography>
                                  </Stack>
                                )}
                              </Stack>
                            </>
                          )}
                        </Stack>

                        {lazyLoadedPermitRequirement.requirements &&
                          lazyLoadedPermitRequirement.requirements.length > 0 && (
                          <>
                            <Divider />
                            <Stack spacing={1}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.primary"
                              >
                                Requirements:
                              </Typography>
                              <Stack spacing={1.5} sx={{ mt: 1 }}>
                                {lazyLoadedPermitRequirement.requirements.map(
                                  (requirement, index) => (
                                    <Box
                                      key={`permit-req-${requirement}`}
                                      sx={{
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'flex-start',
                                      }}
                                    >
                                      <Chip
                                        label={index + 1}
                                        size="small"
                                        sx={{
                                          minWidth: 28,
                                          height: 28,
                                          bgcolor: 'grey.200',
                                          color: 'text.primary',
                                          fontWeight: 600,
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ pt: 0.5 }}
                                      >
                                        {requirement}
                                      </Typography>
                                    </Box>
                                  )
                                )}
                              </Stack>
                            </Stack>
                          </>
                        )}

                        {lazyLoadedPermitRequirement.notes && (
                          <>
                            <Divider />
                            <Box
                              sx={{
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                p: 2,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                💡 {lazyLoadedPermitRequirement.notes}
                              </Typography>
                            </Box>
                          </>
                        )}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            textAlign: 'center',
                            fontStyle: 'italic',
                          }}
                        >
                          Permit requirements may vary. Please verify with your local
                          building department.
                        </Typography>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Vendor Questions Tab */}
              {activeTab === availableTabs.findIndex(tab => tab.value === 'vendor-questions') && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'white',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Loading state */}
                    {vendorQuestionsQuery.isLoading && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 8,
                        }}
                      >
                        <Box
                          component="img"
                          src={questionsLoader}
                          alt="Generating vendor questions"
                          sx={{
                            width: 200,
                            height: 200,
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 2 }}
                        >
                          Generating vendor questions...
                        </Typography>
                      </Box>
                    )}

                    {/* Error state */}
                    {vendorQuestionsQuery.isError && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="error">
                          Failed to load vendor questions
                        </Typography>
                      </Box>
                    )}

                    {/* Not applicable state */}
                    {!vendorQuestionsQuery.isLoading &&
                      !vendorQuestionsQuery.isError &&
                      (!lazyLoadedVendorQuestions ||
                        !lazyLoadedVendorQuestions.questions ||
                        lazyLoadedVendorQuestions.questions.length === 0) && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6">❓ Questions Not Available</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Vendor questions are not available for this service
                        </Typography>
                      </Box>
                    )}

                    {/* Data loaded state */}
                    {lazyLoadedVendorQuestions &&
                      lazyLoadedVendorQuestions.questions &&
                      lazyLoadedVendorQuestions.questions.length > 0 && (
                      <Stack spacing={2.5}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.5rem',
                            }}
                          >
                            ❓
                          </Box>
                          <Stack flex={1}>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 700, color: 'text.primary' }}
                            >
                              Questions to Ask Vendors
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', mt: 0.5 }}
                            >
                              Personalized questions for your specific project
                            </Typography>
                          </Stack>
                        </Stack>

                        <Divider />

                        <Stack spacing={1.5}>
                          {lazyLoadedVendorQuestions.questions.map((question) => (
                            <Stack
                              key={`question-${question}`}
                              direction="row"
                              spacing={1.5}
                              alignItems="flex-start"
                            >
                              <Chip
                                label={
                                  lazyLoadedVendorQuestions.questions.indexOf(question) + 1
                                }
                                size="small"
                                sx={{
                                  minWidth: 28,
                                  height: 28,
                                  bgcolor: 'grey.200',
                                  color: 'text.primary',
                                  fontWeight: 600,
                                  mt: 0.2,
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ flex: 1 }}
                              >
                                {question}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            textAlign: 'center',
                            fontStyle: 'italic',
                            display: 'block',
                            mt: 2,
                          }}
                        >
                          Confidence: {lazyLoadedVendorQuestions.confidence} • Questions
                          tailored to your project and location
                        </Typography>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Stack>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Box>
  );
};

export default VendorSearchResults;
